import { useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { MapPin, LocateFixed, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import StateCitySelect from "./StateCitySelect";
import { useUserLocation } from "../lib/location";
import { INDIA } from "../data/india";

const norm = (s) => (s || "").toLowerCase().replace(/[^a-z]/g, "");
const ALIASES = { bangalore: "bengaluru", bombay: "mumbai", madras: "chennai", calcutta: "kolkata", gurgaon: "gurugram", delhi: "newdelhi", newdelhi: "newdelhi" };

function matchPlace(addr) {
  const stateKey = Object.keys(INDIA).find((s) => norm(s) === norm(addr.state)) || Object.keys(INDIA).find((s) => norm(addr.state).includes(norm(s)));
  if (!stateKey) return null;
  const tries = [addr.city, addr.town, addr.city_district, addr.state_district, addr.county, addr.suburb, addr.village].filter(Boolean).map((x) => ALIASES[norm(x)] || norm(x));
  const city = INDIA[stateKey].find((c) => tries.some((t) => norm(c) === t || t.includes(norm(c)) || norm(c).includes(t)));
  return { state: stateKey, city: city || "" };
}

export default function LocationPrompt() {
  const { isSignedIn } = useAuth();
  const { location, setLocation, askOpen, setAskOpen } = useUserLocation();
  const [sel, setSel] = useState({ state: location?.state || "", city: location?.city || "" });
  const [busy, setBusy] = useState(false);
  const prevSigned = useRef(isSignedIn);

  useEffect(() => {
    // Ask soon after the user signs in, if we don't know their city yet.
    if (isSignedIn && !location?.city && !sessionStorage.getItem("csm_loc_asked")) {
      const t = setTimeout(() => { sessionStorage.setItem("csm_loc_asked", "1"); setAskOpen(true); }, prevSigned.current ? 2500 : 1500);
      return () => clearTimeout(t);
    }
    prevSigned.current = isSignedIn;
  }, [isSignedIn, location, setAskOpen]);

  useEffect(() => { if (askOpen) setSel({ state: location?.state || "", city: location?.city || "" }); }, [askOpen, location]);

  const detect = () => {
    if (!navigator.geolocation) return toast.error("Location is not supported on this device");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&zoom=10&addressdetails=1&accept-language=en&lat=${coords.latitude}&lon=${coords.longitude}`);
        const m = matchPlace((await r.json()).address || {});
        if (!m) toast.error("Couldn't match your location to an Indian city. Please choose it below.");
        else { setSel(m); if (!m.city) toast.message(`Found ${m.state} — please pick your city`); }
      } catch { toast.error("Couldn't look up your location. Please choose it below."); }
      setBusy(false);
    }, () => { setBusy(false); toast.error("Location permission denied. Please choose your city below."); }, { timeout: 15000 });
  };

  const save = () => {
    if (!sel.state || !sel.city) return toast.error("Choose your state and city");
    setLocation(sel);
    setAskOpen(false);
    toast.success(`Showing cars and offers for ${sel.city}`);
  };

  return (
    <Dialog open={askOpen} onOpenChange={setAskOpen}>
      <DialogContent className="bg-[#111319] border-[#D4AF37]/30 text-white sm:max-w-md" data-testid="location-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl gold-text flex items-center gap-2"><MapPin className="w-6 h-6 text-[#D4AF37]" />Where are you?</DialogTitle>
          <DialogDescription className="text-gray-400">We'll show banners, offers and cars for your city.</DialogDescription>
        </DialogHeader>
        <button onClick={detect} disabled={busy} className="btn-gold w-full" data-testid="location-detect-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}Use my current location
        </button>
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500"><span className="flex-1 h-px bg-white/10" />or choose<span className="flex-1 h-px bg-white/10" /></div>
        <div className="grid grid-cols-2 gap-3"><StateCitySelect state={sel.state} city={sel.city} prefix="location-" onChange={(v) => setSel({ ...sel, ...v })} /></div>
        <div className="flex gap-2 justify-end">
          <button className="btn-outline" onClick={() => setAskOpen(false)} data-testid="location-later-btn">Later</button>
          <button className="btn-gold" onClick={save} data-testid="location-save-btn">Save city</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
